import {
  ChallengeNameType,
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  InitiateAuthCommandInput,
  RespondToAuthChallengeRequest,
  RespondToAuthChallengeCommand,
  AuthenticationResultType,
  AssociateSoftwareTokenCommandInput,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  VerifySoftwareTokenCommandInput,
  AuthFlowType,
  ForgotPasswordCommand,
  ForgotPasswordRequest,
  ConfirmForgotPasswordRequest,
  ConfirmForgotPasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import Cookies from 'js-cookie';

export const ID_TOKEN_KEY = 'IdToken';
export const REFRESH_TOKEN_KEY = 'RefreshToken';
export const KEEP_SESSION_KEY = 'KeepSession';

export const AUTH_COOKIE_OPTIONS = {
  path: '/',
  sameSite: 'lax' as const,
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
};

export const CLIENT_ID = process.env.COGNITO_CLIENT_ID;
export const CLIENT_REGION = process.env.COGNITO_REGION;

export const cognitoClient = new CognitoIdentityProviderClient({
  region: CLIENT_REGION,
});

type TSignInResult = {
  redirect?: string;
  error?: unknown;
};

export const clearSession = () => {
  sessionStorage.removeItem('username');
  sessionStorage.removeItem('session');
};

export const errors = Object.freeze({
  userDisabled: 'User is disabled.',
  wrongUserPassword: 'Incorrect username or password.',
  temporaryPasswordExpired: 'Temporary password has expired',
});

const setupSession = (username: string | null | undefined, session: string | null | undefined) => {
  if (username) sessionStorage.setItem('username', username);
  if (session) sessionStorage.setItem('session', session);
};

const getSession = () => {
  const session = {
    username: sessionStorage.getItem('username') || '',
    session: sessionStorage.getItem('session') || '',
  };

  return session;
};

export const signIn = async (username: string, password: string): Promise<TSignInResult> => {
  clearSession();
  const params: InitiateAuthCommandInput = {
    AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
    ClientId: CLIENT_ID,
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password,
    },
  };
  try {
    const command = new InitiateAuthCommand(params);
    const resp = await cognitoClient.send(command);

    if (resp.ChallengeName == null) {
      if (resp.AuthenticationResult) setTokens(resp.AuthenticationResult);
      const savedRedirect = getLoginRedirect();
      return { redirect: savedRedirect || '/app' };
    } else if (resp.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
      setupSession(resp.ChallengeParameters?.USER_ID_FOR_SRP, resp.Session);
      return {
        redirect: '/app/mfa',
      };
    } else if (resp.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      setupSession(username, resp.Session);
      return {
        redirect: '/app/change-password',
      };
    } else if (resp.ChallengeName === 'MFA_SETUP') {
      setupSession(username, resp.Session);
      return {
        redirect: '/app/setup-mfa',
      };
    }
    return { error: resp };
  } catch (error) {
    console.error('Error signing in: ', error);
    return { error };
  }
};

export const refreshToken = async () => {
  clearSession();
  const token = Cookies.get(REFRESH_TOKEN_KEY) || '';

  if (!token) return null;

  const params: InitiateAuthCommandInput = {
    AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
    ClientId: CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: token,
    },
  };
  try {
    const command = new InitiateAuthCommand(params);
    const resp = await cognitoClient.send(command);

    if (resp.ChallengeName == null && resp.AuthenticationResult) {
      setIdToken(resp.AuthenticationResult);
      return resp.AuthenticationResult.IdToken;
    }
  } catch (error) {
    console.error('Error signing in: ', error);
  }
  return null;
};

export const initiateMFASetup = async () => {
  const { session, username } = getSession();

  const params: AssociateSoftwareTokenCommandInput = {
    Session: session,
  };

  try {
    const command = new AssociateSoftwareTokenCommand(params);
    const resp = await cognitoClient.send(command);

    setupSession(null, resp.Session);

    return {
      code: resp.SecretCode,
      session: resp.Session,
      username,
    };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const setupMFA = async ({ totp }: { totp: string }): Promise<TSignInResult> => {
  const { session } = getSession();

  const params: VerifySoftwareTokenCommandInput = {
    Session: session,
    UserCode: totp,
  };

  try {
    const command = new VerifySoftwareTokenCommand(params);
    const resp = await cognitoClient.send(command);

    if (resp.Status === 'SUCCESS') return { redirect: '/app/sign-in' };
    else return { error: resp };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const validateMFA = async (totp: string): Promise<TSignInResult> => {
  const { session, username } = getSession();

  if (!session || !username) return { redirect: '/app/sign-in' };

  const params: RespondToAuthChallengeRequest = {
    ClientId: CLIENT_ID,
    Session: session,
    ChallengeName: ChallengeNameType.SOFTWARE_TOKEN_MFA,
    ChallengeResponses: {
      USERNAME: username,
      SOFTWARE_TOKEN_MFA_CODE: totp,
    },
  };

  try {
    const command = new RespondToAuthChallengeCommand(params);
    const resp = await cognitoClient.send(command);

    if (resp.AuthenticationResult) setTokens(resp.AuthenticationResult);

    const savedRedirect = getLoginRedirect();
    return {
      redirect: savedRedirect || '/app',
    };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const firstResetPassword = async ({ password }: { password: string }): Promise<TSignInResult> => {
  const { session, username } = getSession();
  const params: RespondToAuthChallengeRequest = {
    ClientId: CLIENT_ID,
    Session: session,
    ChallengeName: ChallengeNameType.NEW_PASSWORD_REQUIRED,
    ChallengeResponses: {
      USERNAME: username,
      NEW_PASSWORD: password,
    },
  };

  try {
    const command = new RespondToAuthChallengeCommand(params);
    const resp = await cognitoClient.send(command);

    if (resp.ChallengeName === null && resp.AuthenticationResult) {
      setTokens(resp.AuthenticationResult);
      const savedRedirect = getLoginRedirect();
      return { redirect: savedRedirect || '/app' };
    }

    if (resp.ChallengeName === 'MFA_SETUP') {
      setupSession(null, resp.Session);
      return { redirect: '/app/setup-mfa' };
    }

    return { error: resp };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const forgotPassword = async (username: string) => {
  const params: ForgotPasswordRequest = {
    ClientId: CLIENT_ID,
    Username: username,
  };

  try {
    const command = new ForgotPasswordCommand(params);
    await cognitoClient.send(command);
    return {};
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const changeForgottenPassword = async ({
  username,
  password,
  code,
}: {
  username: string;
  password: string;
  code: string;
}) => {
  const params: ConfirmForgotPasswordRequest = {
    ClientId: CLIENT_ID,
    Username: username,
    Password: password,
    ConfirmationCode: code,
  };

  try {
    const command = new ConfirmForgotPasswordCommand(params);
    await cognitoClient.send(command);
    return { redirect: '/app/sign-in' };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

const setIdToken = (authResult: AuthenticationResultType) => {
  const { IdToken } = authResult;
  if (IdToken) Cookies.set(ID_TOKEN_KEY, IdToken, AUTH_COOKIE_OPTIONS);
};

const setTokens = (authResult: AuthenticationResultType) => {
  clearSession();
  // clearTokens();
  const { IdToken, RefreshToken } = authResult;
  if (IdToken) Cookies.set(ID_TOKEN_KEY, IdToken, AUTH_COOKIE_OPTIONS);
  if (RefreshToken) Cookies.set(REFRESH_TOKEN_KEY, RefreshToken, AUTH_COOKIE_OPTIONS);
};

export const clearTokens = () => {
  Cookies.remove(ID_TOKEN_KEY);
  Cookies.remove(REFRESH_TOKEN_KEY);
};

export const setLoginRedirect = (redirect: string | null) => {
  if (redirect) sessionStorage.setItem('loginRedirect', redirect);
  else sessionStorage.removeItem('loginRedirect');
};

export const getLoginRedirect = () => {
  let lr = sessionStorage.getItem('loginRedirect');
  if (lr) lr = `${lr}`;
  setLoginRedirect(null);
  // setTimeout(() => setLoginRedirect(null), 500);
  return lr;
};

export type OAuthHashParams = {
  id_token?: string;
  error?: string;
  error_description?: string;
};

/**
 * Parses OAuth callback hash fragment. Uses URLSearchParams to correctly handle
 * values containing '=' (e.g. base64 padding in tokens).
 */
export const parseOAuthHash = (hash: string): OAuthHashParams => {
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return {};
  const params = new URLSearchParams(trimmed);
  const result: OAuthHashParams = {};
  const idToken = params.get('id_token');
  if (idToken) result.id_token = idToken;
  const error = params.get('error');
  if (error) result.error = error;
  const errorDescription = params.get('error_description');
  if (errorDescription) result.error_description = errorDescription;
  return result;
};

export const decodeJWT = (token: any) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
      .join(''),
  );
  return JSON.parse(jsonPayload);
};
